/*
 * Who can play, buy or subscribe on the video detail page.
 * Every rule below is a one-to-one copy of a condition in album-list.component.html
 * (video list + header buttons) so the new page keeps the same business rules.
 * Loose equality (==) is kept on purpose: the API sends some of these values as strings.
 */

export interface VideoAccessContext {
  streamInfo: any;
  isPreview: boolean;
  showCountDownTimer: boolean;
  showAlbumPaymentButton: boolean;
  showAlbumSubscriptionButton: boolean;
  subscribedUserPlans: any[];
  isLoggedIn: boolean;
}

export interface VideoRowAccess {
  saleNotStarted: boolean;
  canPlay: boolean;
  previewPlay: boolean;
  previewBuy: boolean;
  buy: boolean;
  previewSubscribe: boolean;
  subscribe: boolean;
  planInsufficient: boolean;
  albumLocked: boolean;
  canAddToPlaylist: boolean;
  canLike: boolean;
}

export interface AlbumActions {
  showActions: boolean;
  showPlay: boolean;
  playDisabled: boolean;
  showAlbumSubscribe: boolean;
  showAlbumBuy: boolean;
  tipKind: 'tip' | 'donation' | null;
  showPolls: boolean;
  showFloatingSubscribe: boolean;
  /** Subscription collection marker beside Play (display only). */
  subscriptionChip: 'Subscription' | 'Subscribed' | null;
}

function albumUnlockedByPurchase(ctx: VideoAccessContext): boolean {
  return ctx.streamInfo?.stream_payment_mode == 'paid' && !ctx.showAlbumPaymentButton;
}

function albumUnlockedBySubscription(ctx: VideoAccessContext): boolean {
  return ctx.streamInfo?.stream_payment_mode == 'subscription' && !ctx.showAlbumSubscriptionButton;
}

export function videoRowAccess(element: any, ctx: VideoAccessContext): VideoRowAccess {
  const mode = element?.stream_info?.stream_payment_mode;
  const isFree = !mode || mode == 'free' || mode == 'free_ads';
  const preview = ctx.isPreview;
  const albumBought = albumUnlockedByPurchase(ctx);
  const albumSubscribed = albumUnlockedBySubscription(ctx);

  const canPlay = ((isFree || element.isSubscribed || element.isPurchased) && !preview)
    || (mode == 'paid' && element.isPurchased)
    || albumBought
    || albumSubscribed;

  const access: VideoRowAccess = {
    saleNotStarted: ctx.showCountDownTimer,
    canPlay,
    previewPlay: (isFree || element.isSubscribed) && preview,
    previewBuy: mode == 'paid' && !element.isPurchased && preview,
    buy: mode == 'paid' && !element.isPurchased && !preview && !albumBought,
    previewSubscribe: mode == 'subscription' && preview,
    subscribe: mode == 'subscription' && !element.isSubscribed && !element.planNotSuff && !preview
      && !albumSubscribed && !albumBought,
    planInsufficient: mode == 'subscription' && !!element.planNotSuff && (ctx.subscribedUserPlans || []).length > 0
      && !preview && !albumSubscribed && !albumBought,
    albumLocked: ctx.streamInfo?.stream_payment_mode == 'paid' && ctx.showAlbumPaymentButton && mode == 'folder',
    canAddToPlaylist: ctx.isLoggedIn && canPlay,
    canLike: !ctx.showCountDownTimer,
  };
  return access;
}

/** Every context value the rules above read, so results can be cached safely. */
export function accessContextKey(ctx: VideoAccessContext): string {
  const s = ctx.streamInfo || {};
  return [s.id, s.stream_payment_mode, s.vod_video_audio, s.tip_donation, ctx.isPreview, ctx.showCountDownTimer,
    ctx.showAlbumPaymentButton, ctx.showAlbumSubscriptionButton, (ctx.subscribedUserPlans || []).length, ctx.isLoggedIn].join('|');
}

/** Every row value videoRowAccess reads. */
export function rowAccessKey(element: any): string {
  return [element?.stream_info?.stream_payment_mode, element?.isSubscribed, element?.isPurchased, element?.planNotSuff].join('|');
}

/** Tip / donation choice, same as the header icons on the classic page. */
export function tipKindFor(streamInfo: any, performerDetails: any): 'tip' | 'donation' | null {
  const value = streamInfo?.tip_donation;
  const usePerformerDefault = value != 0 && value != 1 && value != 2;
  if (value == 1 || (usePerformerDefault && performerDetails?.tip_donation == 1)) return 'tip';
  if (value == 2 || (usePerformerDefault && performerDetails?.tip_donation == 2)) return 'donation';
  return null;
}

/**
 * "Subscribed" only for a signed-in fan whose plan covers the album (the Subscribe button is hidden for it).
 * No chip while the Subscribe button shows - beside it the chip read as a second button (QA 2026-10-27).
 */
export function subscriptionChipFor(ctx: VideoAccessContext): 'Subscription' | 'Subscribed' | null {
  if (ctx.streamInfo?.stream_payment_mode != 'subscription') return null;
  if (ctx.showAlbumSubscriptionButton && !ctx.isPreview) return null;
  const subscribed = ctx.isLoggedIn && !ctx.showAlbumSubscriptionButton && (ctx.subscribedUserPlans || []).length > 0;
  return subscribed ? 'Subscribed' : 'Subscription';
}

export function albumActions(ctx: VideoAccessContext, extra: {
  subEvents: any[]; tagCategory: string; enablePlay: boolean; performerDetails: any;
  isSubscribed: boolean; settingsPreview: boolean;
}): AlbumActions {
  const mode = ctx.streamInfo?.stream_payment_mode;
  const showActions = !ctx.showCountDownTimer;
  const subEvents = extra.subEvents || [];
  return {
    showActions,
    showPlay: showActions && subEvents.length > 0 && extra.tagCategory != 'recorded-video',
    playDisabled: !!extra.enablePlay || ctx.isPreview || (mode == 'subscription' && ctx.showAlbumSubscriptionButton),
    showAlbumSubscribe: showActions && mode == 'subscription' && ctx.showAlbumSubscriptionButton && !ctx.isPreview,
    showAlbumBuy: showActions && mode == 'paid' && ctx.showAlbumPaymentButton && !ctx.isPreview,
    tipKind: showActions && !ctx.isPreview ? tipKindFor(ctx.streamInfo, extra.performerDetails) : null,
    showPolls: showActions && ctx.streamInfo?.vod_video_audio == 'video'
      && subEvents[0]?.stream_info?.polls?.length > 0 && !ctx.isPreview,
    showFloatingSubscribe: !extra.isSubscribed && !extra.settingsPreview && !ctx.isPreview,
    subscriptionChip: subscriptionChipFor(ctx),
  };
}
